import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    Typography,
    CircularProgress,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useTheme } from "@mui/material/styles";
import axios from "axios";

// Full set of categories
const CATEGORY_OPTIONS = [
    "SIS", "POS", "Banking", "Accounting", "Payroll",
    "Procurement", "Messaging", "Analytics", "SSO", "USDA",
];

// Status options
const STATUS_OPTIONS = ["Planned", "Active", "Deprecated"];

// Auth types
const AUTH_OPTIONS = ["OAuth2", "API Key", "None"];

export default function IntegrationsPage() {
    const theme = useTheme();
    const isLight = theme.palette.mode === "light";

    // Button styling: invert for light/dark
    const buttonSx = {
        backgroundColor: isLight ? theme.palette.grey[900] : theme.palette.grey[100],
        color: isLight ? theme.palette.common.white : theme.palette.common.black,
        textTransform: "none",
        "&:hover": {
            backgroundColor: isLight ? theme.palette.grey[800] : theme.palette.grey[300],
        },
    };

    const apiBase = "https://figazuresql.swancreate.com/api/swa/FINANCE_Integrations";

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterCategory, setFilterCategory] = useState("");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState({
        IntegrationName: "",
        website: "",
        username: "",
        password: "",
        IntegrationCategory: "",
        Description: "",
        ApiEndpoint: "",
        ApiKey: "",
        AuthType: "",
        IntegrationStatus: "Planned",
        DocumentationURL: "",
        LogoFile: null,
    });

    // Fetch all integrations
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            console.log(`GET ${apiBase}`);
            const { data } = await axios.get(apiBase);
            setRows(data);
        } catch (err) {
            console.error("Fetch error:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((f) => ({ ...f, [name]: value }));
    };

    // Helper to upload file to media API (ignore response)
    const uploadToMedia = async (file) => {
        console.log("Uploading logo:", file);
        const data = new FormData();
        data.append("file", file);
        data.append("folder", "figfinance");
        await axios.post("https://media.swancreate.com/upload", data, {
            headers: { "Content-Type": "multipart/form-data" },
        });
    };

    // Upload + save logoPath (using file.name)
    const uploadLogoAndSave = async (integrationId, file) => {
        const row = rows.find(r => r.IntegrationId === integrationId);
        if (!row) return;
        const { apiid } = row;
        await uploadToMedia(file);
        const logoPath = `https://media.swancreate.com/images/figfinance/${file.name}`;
        console.log(`PATCH ${apiBase}/${apiid}`, { LogoPath: logoPath });
        await axios.patch(`${apiBase}/${apiid}`, { LogoPath: logoPath });
        fetchData();
    };

    // Create integration (no apiid, DB sets it)
    const handleSubmit = async () => {
        try {
            console.log("Creating integration:", form);
            let logoPath = "";
            if (form.LogoFile) {
                await uploadToMedia(form.LogoFile);
                logoPath = `https://media.swancreate.com/images/figfinance/${form.LogoFile.name}`;
            }
            const payload = {
                IntegrationName: form.IntegrationName,
                website: form.website,
                username: form.username,
                password: form.password,
                IntegrationCategory: form.IntegrationCategory,
                Description: form.Description,
                ApiEndpoint: form.ApiEndpoint,
                ApiKey: form.ApiKey,
                AuthType: form.AuthType,
                IntegrationStatus: form.IntegrationStatus,
                LogoPath: logoPath,
                DocumentationURL: form.DocumentationURL,
            };
            console.log(`POST ${apiBase}`, payload);
            await axios.post(apiBase, payload);
            setDialogOpen(false);
            fetchData();
        } catch (err) {
            console.error("Create error:", err);
        }
    };

    // Inline edit with PATCH
    const handleRowEdit = async ({ id, field, value }) => {
        const row = rows.find(r => r.IntegrationId === id);
        if (!row) return;
        const { apiid } = row;
        console.log(`PATCH ${apiBase}/${apiid}`, { [field]: value });
        try {
            await axios.patch(`${apiBase}/${apiid}`, { [field]: value });
            fetchData();
        } catch (err) {
            console.error("Update error:", err);
        }
    };

    // Logo thumbnail cell
    const LogoThumbnailCell = ({ row }) =>
        row.LogoPath ? (
            <img src={row.LogoPath} alt="" style={{ maxHeight: 40, borderRadius: 4 }} />
        ) : null;

    // Replace-logo button cell
    const ReplaceLogoCell = ({ row }) => {
        const [uploading, setUploading] = useState(false);
        const inputRef = React.useRef();
        const onFileSelected = async (e) => {
            const file = e.target.files[0];
            if (!file) return;
            setUploading(true);
            try {
                await uploadLogoAndSave(row.IntegrationId, file);
            } finally {
                setUploading(false);
            }
        };
        return (
            <>
                <input
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    ref={inputRef}
                    onChange={onFileSelected}
                />
                <Button
                    size="small"
                    variant="contained"
                    sx={buttonSx}
                    onClick={() => inputRef.current.click()}
                    disabled={uploading}
                >
                    {uploading ? "Uploading..." : row.LogoPath ? "Replace" : "Upload"}
                </Button>
            </>
        );
    };

    // Columns
    const columns = [
        { field: "IntegrationId", headerName: "ID", width: 70 },
        { field: "IntegrationName", headerName: "Name", flex: 1, editable: true },
        { field: "website", headerName: "Website", flex: 1, editable: true },
        {
            field: "openSite",
            headerName: "Open Site",
            width: 120,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Button
                    size="small"
                    variant="contained"
                    sx={buttonSx}
                    onClick={() => window.open(params.row.website, "_blank")}
                >
                    Open
                </Button>
            ),
        },
        { field: "username", headerName: "Username", flex: 1, editable: true },
        { field: "password", headerName: "Password", flex: 1, editable: true },
        {
            field: "IntegrationCategory",
            headerName: "Category",
            width: 140,
            editable: true,
            type: "singleSelect",
            valueOptions: CATEGORY_OPTIONS,
        },
        { field: "Description", headerName: "Description", flex: 2, editable: true },
        { field: "ApiEndpoint", headerName: "Endpoint", flex: 1, editable: true },
        { field: "ApiKey", headerName: "API Key", flex: 1, editable: true },
        {
            field: "AuthType",
            headerName: "Auth Type",
            width: 120,
            editable: true,
            type: "singleSelect",
            valueOptions: AUTH_OPTIONS,
        },
        {
            field: "IntegrationStatus",
            headerName: "Status",
            width: 120,
            editable: true,
            type: "singleSelect",
            valueOptions: STATUS_OPTIONS,
        },
        {
            field: "LogoPath",
            headerName: "Logo",
            flex: 1,
            renderCell: (params) => <LogoThumbnailCell {...params} />,
            sortable: false,
            filterable: false,
        },
        {
            field: "replaceLogo",
            headerName: "Replace Logo",
            width: 140,
            sortable: false,
            filterable: false,
            renderCell: (params) => <ReplaceLogoCell {...params} />,
        },
        { field: "DocumentationURL", headerName: "Docs URL", flex: 1, editable: true },
    ];

    const displayedRows = filterCategory
        ? rows.filter((r) => r.IntegrationCategory === filterCategory)
        : rows;

    return (
        <Box p={2}>
            {/* Header */}
            <Box display="flex" alignItems="center" mb={2} gap={2}>
                <Typography variant="h5">Integrations</Typography>
                <FormControl variant="standard" size="small">
                    <InputLabel>Filter Category</InputLabel>
                    <Select
                        value={filterCategory}
                        onChange={(e) => setFilterCategory(e.target.value)}
                        label="Filter Category"
                        sx={{ width: 180 }}
                    >
                        <MenuItem value="">All</MenuItem>
                        {CATEGORY_OPTIONS.map((opt) => (
                            <MenuItem key={opt} value={opt}>
                                {opt}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Box flexGrow={1} />
                <Button variant="contained" sx={buttonSx} onClick={() => setDialogOpen(true)}>
                    + New Integration
                </Button>
            </Box>

            {/* DataGrid */}
            {loading ? (
                <CircularProgress />
            ) : (
                <Box
                    sx={{
                        height: 600,
                        width: "100%",
                        "& .even": { backgroundColor: theme.palette.action.hover },
                    }}
                >
                    <DataGrid
                        rows={displayedRows}
                        columns={columns}
                        getRowId={(r) => r.IntegrationId}
                        components={{ Toolbar: GridToolbar }}
                        disableSelectionOnClick
                        editMode="cell"
                        onCellEditCommit={handleRowEdit}
                        getRowClassName={(params) =>
                            params.indexRelativeToCurrentPage % 2 === 0 ? "even" : ""
                        }
                    />
                </Box>
            )}

            {/* New Integration Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>New Integration</DialogTitle>
                <DialogContent dividers>
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                        <TextField
                            label="Name"
                            name="IntegrationName"
                            value={form.IntegrationName}
                            onChange={handleChange}
                            fullWidth
                        />
                        <TextField
                            label="Website"
                            name="website"
                            value={form.website}
                            onChange={handleChange}
                            fullWidth
                        />
                        <TextField
                            label="Username"
                            name="username"
                            value={form.username}
                            onChange={handleChange}
                            fullWidth
                        />
                        <TextField
                            label="Password"
                            name="password"
                            type="password"
                            value={form.password}
                            onChange={handleChange}
                            fullWidth
                        />
                        <FormControl fullWidth>
                            <InputLabel>Category</InputLabel>
                            <Select
                                label="Category"
                                name="IntegrationCategory"
                                value={form.IntegrationCategory}
                                onChange={handleChange}
                            >
                                {CATEGORY_OPTIONS.map((opt) => (
                                    <MenuItem key={opt} value={opt}>
                                        {opt}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Description"
                            name="Description"
                            value={form.Description}
                            onChange={handleChange}
                            multiline
                            rows={2}
                            fullWidth
                        />
                        <TextField
                            label="API Endpoint"
                            name="ApiEndpoint"
                            value={form.ApiEndpoint}
                            onChange={handleChange}
                            fullWidth
                        />
                        <TextField
                            label="API Key"
                            name="ApiKey"
                            value={form.ApiKey}
                            onChange={handleChange}
                            fullWidth
                        />
                        <FormControl fullWidth>
                            <InputLabel>Auth Type</InputLabel>
                            <Select
                                label="Auth Type"
                                name="AuthType"
                                value={form.AuthType}
                                onChange={handleChange}
                            >
                                {AUTH_OPTIONS.map((opt) => (
                                    <MenuItem key={opt} value={opt}>
                                        {opt}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                                label="Status"
                                name="IntegrationStatus"
                                value={form.IntegrationStatus}
                                onChange={handleChange}
                            >
                                {STATUS_OPTIONS.map((opt) => (
                                    <MenuItem key={opt} value={opt}>
                                        {opt}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Docs URL"
                            name="DocumentationURL"
                            value={form.DocumentationURL}
                            onChange={handleChange}
                            fullWidth
                        />
                        {/* Logo upload in dialog */}
                        <TextField
                            type="file"
                            inputProps={{ accept: "image/*" }}
                            onChange={(e) => setForm((f) => ({ ...f, LogoFile: e.target.files[0] }))}
                        />
                        {form.LogoFile && <Typography>{form.LogoFile.name}</Typography>}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)} sx={buttonSx}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleSubmit} sx={buttonSx}>
                        Create
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
